import IFinancingProvider, { Estimate } from '@/services/Financing/IFinancingProvider';
import AffirmUtils from '@/services/Financing/AffirmUtils';
import PayBrightUtils from '@/services/Financing/PayBrightUtils';
import CountryUtils from '@/components/commons/CountryUtils.js';
import useCommon from '@/compositions/useCommon';
import { computed, onMounted, watch } from 'vue';

export const CONTENTFUL_FINANCING_MESSAGE_SLUG = 'financing-callout';
export const CONTENTFUL_FINANCING_CTA_SLUG = 'financing-callout-cta-text';

export default function useFinancing() {
  const { financing: config, countryId } = useCommon();

  const providers = [
    {
      provider: 'affirm',
      utils: AffirmUtils,
      minAmount: config.value.affirmMinAmount,
      maxAmount: config.value.affirmMaxAmount,
      isAvailable: computed(() => {
        return config.value && config.value.affirmEnabled && CountryUtils.US.id === countryId.value;
      }),
      load: () => {
        AffirmUtils.load(config.value.affirmKey, config.value.affirmUrl);
      },
    },
    {
      provider: 'pb',
      utils: PayBrightUtils,
      minAmount: config.value.paybrightMinAmount,
      maxAmount: config.value.paybrightMaxAmount,
      isAvailable: computed(() => {
        return config.value && config.value.paybrightEnabled && CountryUtils.CA.id === countryId.value;
      }),
      load: () => {
        PayBrightUtils.load(config.value.paybrightKey, config.value.paybrightUrl, config.value);
      },
    },
  ];

  const load = async () => {
    providers.filter((p) => p.isAvailable.value).forEach((p) => p.load());
  };

  onMounted(() => load());
  watch(() => config, load);

  const isEnabled = (amount): Boolean => {
    return providers.some((p) => {
      return p.isAvailable.value && inRange(amount, p.minAmount, p.maxAmount);
    });
  };

  const availableProvider = computed((): IFinancingProvider => {
    return providers.find((p) => p.isAvailable.value)?.utils;
  });

  const getEnabledProviderItem = (amount) => {
    return providers.find((p) => p.isAvailable.value && inRange(amount, p.minAmount, p.maxAmount));
  };

  const estimatePayment = async (amountInCents, options): Promise<Estimate> => {
    const provider = await getEnabledProviderItem(amountInCents / 100);
    if (provider?.utils) {
      return provider?.utils?.estimatePayment(amountInCents, config.value, options);
    }
    return {
      estimateAvailable: false,
      provider: 'none',
      modalUrl: '',
      monthlyPayment: 0,
      openModal: () => {},
      openNativeModal: () => {},
    };
  };

  const prequalifyModalUrl = computed(() => {
    return availableProvider.value?.preQualifyModalUrl();
  });

  const openPrequalifyModal = () => {
    return availableProvider.value?.openPrequalifyModal();
  };

  const inRange = (amount, min, max) => {
    const amountDollars = amount;
    return amountDollars >= min && amountDollars <= max;
  };

  return {
    isAffirmEnabled: (amount) => getEnabledProviderItem(amount)?.provider === 'affirm',
    isPaybrightEnabled: (amount) => getEnabledProviderItem(amount)?.provider === 'pb',
    isAffirmAvailable: computed(() => providers.some((p) => p.provider === 'affirm' && p.isAvailable.value)),
    isPaybrightAvailable: computed(() => providers.some((p) => p.provider === 'pb' && p.isAvailable.value)),
    //
    load,
    isEnabled,
    availableProvider,
    getEnabledProvider: (amount) => getEnabledProviderItem(amount)?.utils,
    estimatePayment,
    prequalifyModalUrl,
    openPrequalifyModal,
  };
}
